import { PixelStreamingHandler } from './PixelStreamingHandler';
import * as Sentry from '@sentry/react';

export type TeleportLocation = 'outside' | 'stairwell' | 'apartment';

export interface MessageLog {
  timestamp: string;
  direction: 'sent' | 'received';
  message: any;
}

export class PixelStreamingCommunicator {
  private pixelStreamingHandler: PixelStreamingHandler;
  private moveInterval: NodeJS.Timeout | null = null;
  private readonly MOVE_INTERVAL_MS = 100; // Send movement updates every 100ms
  private currentTeleportLocation: TeleportLocation | null = null;
  private isFurnitureVisible: boolean = true;
  private onRotationUpdate: ((rotation: number) => void) | null = null;
  private onFocusedUnitUpdate: ((unitId: string | null) => void) | null = null;
  private onMessageUpdate: ((messages: MessageLog[]) => void) | null = null;
  private messageLog: MessageLog[] = [];
  private readonly MAX_MESSAGES = 50; // Keep last 50 messages
  private onEndLoadingScreen: (() => void) | null = null;
  private endLoadingScreenListeners: Map<string, () => void> = new Map();
  private isStreamReady: boolean = false;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private readonly HEALTH_CHECK_INTERVAL_MS = 5000; // Send health check every 5 seconds
  private hasReceivedHealthCheckResponse: boolean = false;

  constructor(pixelStreamingHandler: PixelStreamingHandler) {
    this.pixelStreamingHandler = pixelStreamingHandler;
  }

  public startListeningToResponses(): void {
    if (this.pixelStreamingHandler && typeof this.pixelStreamingHandler.getApplicationResponse === 'function') {
      this.pixelStreamingHandler.getApplicationResponse((response: any) => {
        
        let parsedResponse = response;
        if (typeof response === 'string') {
          try {
            parsedResponse = JSON.parse(response);
          } catch (e) {
            console.error('[PixelStreamingCommunicator] Failed to parse response as JSON:', response, e);
            // Capture JSON parsing errors
            Sentry.captureException(e instanceof Error ? e : new Error('JSON parse error'), {
              extra: {
                rawResponse: response,
                responseType: typeof response
              },
              tags: {
                component: 'pixelstreaming_communicator',
                operation: 'parse_response'
              }
            });
            // Don't return, try to process as is, or handle error appropriately
          }
        }
        
        // Log non-duplicate messages only
        this.logMessage('received', parsedResponse);
        
        if (parsedResponse && parsedResponse.type === 'focusedUnit') {
          console.log('Received focused unit update:', parsedResponse.data?.unitId);
          this.handleFocusedUnitUpdate(parsedResponse.data?.unitId);
        } else if (parsedResponse && parsedResponse.type === 'rotation') {
          this.handleRotationUpdate(parsedResponse.data?.rotation);
        } else if (parsedResponse && parsedResponse.type === 'EndLoadingScreen') {
          console.log('PixelStreamingCommunicator: Received EndLoadingScreen event');
          // Notify all registered listeners
          this.endLoadingScreenListeners.forEach(listener => listener());
          // Also call the single listener for backward compatibility if needed, or phase out
          if (this.onEndLoadingScreen) {
            this.onEndLoadingScreen();
          }
        } else if (parsedResponse && parsedResponse.type.toLowerCase() === 'healthcheckresponse') {
          console.log('PixelStreamingCommunicator: Received HealthCheckResponse');
          this.hasReceivedHealthCheckResponse = true;
          this.stopHealthCheck(); // Stop sending health checks once we get a response

          // If EndLoadingScreen hasn't been processed, and we get a health check response,
          // assume the stream is ready, especially for reconnects.
          if (!this.isStreamReady) {
            console.warn('PixelStreamingCommunicator: HealthCheckResponse received and stream was not marked as ready. Assuming stream is now ready (e.g., reconnect). Triggering EndLoadingScreen logic.');
            this.endLoadingScreenListeners.forEach(listener => listener());
            if (this.onEndLoadingScreen) {
              this.onEndLoadingScreen();
            }
            this.isStreamReady = true; // Ensure stream is marked as ready
          }
        }
      });
    }
  }

  private logMessage(direction: 'sent' | 'received', message: any): void {
    // Check if this is a duplicate message of the same type
    const lastMessage = this.messageLog[0]; // Get the most recent message
    const isDuplicate = lastMessage && 
      lastMessage.direction === direction && 
      lastMessage.message.type === message.type;

    // Only log if it's not a duplicate of the immediately previous message
    if (!isDuplicate) {
      const timestamp = new Date().toISOString();
      this.messageLog.unshift({ timestamp, direction, message });
      
      // Keep only the last MAX_MESSAGES messages
      if (this.messageLog.length > this.MAX_MESSAGES) {
        this.messageLog = this.messageLog.slice(0, this.MAX_MESSAGES);
      }

      // Notify listeners
      if (this.onMessageUpdate) {
        this.onMessageUpdate([...this.messageLog]);
      }
    }
  }

  public setMessageUpdateCallback(callback: (messages: MessageLog[]) => void): void {
    this.onMessageUpdate = callback;
  }

  public getMessageLog(): MessageLog[] {
    return [...this.messageLog];
  }

  private handleRotationUpdate(rotation: number): void {
    if (this.onRotationUpdate) {
      this.onRotationUpdate(rotation);
    }
  }

  private handleFocusedUnitUpdate(unitId: string | null): void {
    console.log('Handling focused unit update with ID:', unitId);
    
    if (!unitId) {
      console.warn('Focused unit update received with null or undefined unitId');
    }
    
    if (this.onFocusedUnitUpdate) {
      console.log('Calling focused unit update callback with ID:', unitId);
      try {
        this.onFocusedUnitUpdate(unitId);
        console.log('Focused unit update callback completed successfully');
      } catch (error) {
        console.error('Error in focused unit update callback:', error);
        Sentry.captureException(error instanceof Error ? error : new Error('Focused unit update callback error'), {
          extra: {
            unitId,
            hasCallback: !!this.onFocusedUnitUpdate
          },
          tags: {
            component: 'pixelstreaming_communicator',
            operation: 'focused_unit_update'
          }
        });
      }
    } else {
      console.log('No focused unit update callback registered');
    }
  }

  public setRotationUpdateCallback(callback: (rotation: number) => void): void {
    this.onRotationUpdate = callback;
  }

  public setFocusedUnitUpdateCallback(callback: (unitId: string | null) => void): void {
    this.onFocusedUnitUpdate = callback;
  }

  public addEndLoadingScreenListener(callback: () => void): string {
    const listenerId = `listener-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    this.endLoadingScreenListeners.set(listenerId, callback);
    console.log(`PixelStreamingCommunicator: Added EndLoadingScreen listener with ID: ${listenerId}`);
    return listenerId;
  }

  public removeEndLoadingScreenListener(listenerId: string): void {
    if (this.endLoadingScreenListeners.has(listenerId)) {
      this.endLoadingScreenListeners.delete(listenerId);
      console.log(`PixelStreamingCommunicator: Removed EndLoadingScreen listener with ID: ${listenerId}`);
    } else {
      console.warn(`PixelStreamingCommunicator: No EndLoadingScreen listener found with ID: ${listenerId}`);
    }
  }

  // Method for BannerComponent to signal when the stream is fully interactive
  public setStreamIsReady(isReady: boolean): void {
    this.isStreamReady = isReady;
  }

  // Start moving in a direction (call this when button is pressed)
  public startMoving(direction: 'left' | 'right'): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot start moving.');
      return;
    }
    if (this.moveInterval) return; // Already moving

    // Send initial movement
    this.sendMovement(direction);

    // Set up interval for continuous movement
    this.moveInterval = setInterval(() => {
      this.sendMovement(direction);
    }, this.MOVE_INTERVAL_MS);
  }

  // Stop moving (call this when button is released)
  public stopMoving(): void {
    if (this.moveInterval) {
      clearInterval(this.moveInterval);
      this.moveInterval = null;
    }
  }

  private sendMovement(direction: 'left' | 'right'): void {
    if (!this.isStreamReady) return; // Also guard here for safety, though startMoving should catch it
    const message = { direction };
    this.logMessage('sent', { type: 'move', data: message });
    this.pixelStreamingHandler.sendEvent('move', message);
  }

  // Method to focus on a specific unit
  public focusUnit(unitId: string): void {
    Sentry.startSpan({
      op: 'pixelstreaming.command',
      name: 'Focus Unit Command'
    }, (span: any) => {
      span.setAttribute('unitId', unitId);
      span.setAttribute('streamReady', this.isStreamReady);
      
      if (!this.isStreamReady) {
        console.warn('PixelStreamingCommunicator: Stream not ready, cannot focus unit.');
        Sentry.captureMessage('Focus unit command ignored - stream not ready', {
          level: 'warning',
          extra: { unitId, isStreamReady: this.isStreamReady },
          tags: { component: 'pixelstreaming_communicator', operation: 'focus_unit' }
        });
        return;
      }
      const message = { unitId };
      this.logMessage('sent', { type: 'focusUnit', data: message });
      this.pixelStreamingHandler.sendEvent('focusUnit', message);
    });
  }

  // Method to reset focus
  public resetFocus(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot reset focus.');
      return;
    }
    this.logMessage('sent', { type: 'resetFocus', data: {} });
    this.pixelStreamingHandler.sendEvent('resetFocus', {});
  }

  // Method to update the list of visible units
  public updateVisibleUnits(unitIds: string[], bIsFiltered: boolean): void {
    Sentry.startSpan({
      op: 'pixelstreaming.command',
      name: 'Update Visible Units'
    }, (span: any) => {
      span.setAttribute('unitCount', unitIds.length);
      span.setAttribute('isFiltered', bIsFiltered);
      span.setAttribute('streamReady', this.isStreamReady);
      
      // console.log(`[Communicator DEBUG] updateVisibleUnits called. Internal isStreamReady: ${this.isStreamReady}`); // Keep this commented out for potential future debugging
      if (!this.isStreamReady) {
        console.warn('PixelStreamingCommunicator: Stream not ready, cannot update visible units.');
        Sentry.captureMessage('Update visible units command ignored - stream not ready', {
          level: 'warning',
          extra: { unitCount: unitIds.length, bIsFiltered, isStreamReady: this.isStreamReady },
          tags: { component: 'pixelstreaming_communicator', operation: 'update_visible_units' }
        });
        return;
      }
      const message = { unitIds, bIsFiltered };
      this.logMessage('sent', { type: 'updateVisibleUnits', data: message });
      this.pixelStreamingHandler.sendEvent('updateVisibleUnits', message);
    });
  }

  // Teleport to a specific location
  public teleportTo(location: TeleportLocation): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot teleport.');
      return;
    }
    const message = { location };
    this.logMessage('sent', { type: 'teleport', data: message });
    this.currentTeleportLocation = location;
    this.pixelStreamingHandler.sendEvent('teleport', message);
  }

  // Get current teleport location
  public getCurrentTeleportLocation(): TeleportLocation | null {
    return this.currentTeleportLocation;
  }

  // Toggle furniture visibility
  public toggleFurniture(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot toggle furniture.');
      return;
    }
    this.isFurnitureVisible = !this.isFurnitureVisible;
    const message = { bIsVisible: this.isFurnitureVisible };
    this.logMessage('sent', { type: 'toggleFurniture', data: message });
    this.pixelStreamingHandler.sendEvent('toggleFurniture', message);
  }

  // Get current furniture visibility state
  public isFurnitureToggled(): boolean {
    return this.isFurnitureVisible;
  }

  // Go back to exterior view
  public goBackToExterior(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot go back to exterior.');
      return;
    }
    this.logMessage('sent', { type: 'backToExterior', data: {} });
    this.pixelStreamingHandler.sendEvent('backToExterior', {});
    // Reset interior-specific states
    this.currentTeleportLocation = null;
    this.isFurnitureVisible = true;
  }

  // Method to start a tour
  public startTour(unitId: string, tourType: 'guided' | 'manual'): void {
    Sentry.startSpan({
      op: 'pixelstreaming.command',
      name: 'Start Tour'
    }, (span: any) => {
      span.setAttribute('unitId', unitId);
      span.setAttribute('tourType', tourType);
      span.setAttribute('streamReady', this.isStreamReady);
      
      if (!this.isStreamReady) {
        console.warn('PixelStreamingCommunicator: Stream not ready, cannot start tour.');
        Sentry.captureMessage('Start tour command ignored - stream not ready', {
          level: 'warning',
          extra: { unitId, tourType, isStreamReady: this.isStreamReady },
          tags: { component: 'pixelstreaming_communicator', operation: 'start_tour' }
        });
        return;
      }
      const message = { unitId, tourType };
      this.logMessage('sent', { type: 'startTour', data: message });
      this.pixelStreamingHandler.sendEvent('startTour', message);
      
      // Force update the message log to ensure UI updates
      if (this.onMessageUpdate) {
        this.onMessageUpdate([...this.messageLog]);
      }
    });
  }

  // Start free roam exploration for a specific house
  public startFreeRoam(house: 'House1' | 'House2'): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot start free roam.');
      return;
    }
    const message = house;
    this.logMessage('sent', { type: 'StartFreeRoam', data: message });
    this.pixelStreamingHandler.sendEvent('StartFreeRoam', message);
  }

  // Start sending health checks
  public startHealthCheck(): void {
    if (this.healthCheckInterval) {
      console.warn('PixelStreamingCommunicator: Health check already running');
      return;
    }

    console.log('PixelStreamingCommunicator: Starting health check');
    this.hasReceivedHealthCheckResponse = false;
    
    // Send initial health check
    this.sendHealthCheck();

    // Set up interval for continuous health checks
    this.healthCheckInterval = setInterval(() => {
      this.sendHealthCheck();
    }, this.HEALTH_CHECK_INTERVAL_MS);
  }

  // Stop sending health checks
  public stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('PixelStreamingCommunicator: Stopped health check');
    }
  }

  // Send a single health check
  private sendHealthCheck(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot send health check');
      // Don't spam Sentry with health check warnings as these are frequent
      return;
    }
    const message = { timestamp: Date.now() };
    this.logMessage('sent', { type: 'HealthCheck', data: message });
    try {
      this.pixelStreamingHandler.sendEvent('HealthCheck', message);
    } catch (error) {
      console.error('PixelStreamingCommunicator: Failed to send health check:', error);
      Sentry.captureException(error instanceof Error ? error : new Error('Health check send error'), {
        extra: { timestamp: message.timestamp },
        tags: { component: 'pixelstreaming_communicator', operation: 'health_check' }
      });
    }
  }

  // Check if we've received a health check response
  public hasReceivedHealthCheck(): boolean {
    return this.hasReceivedHealthCheckResponse;
  }

  // Override cleanup to include health check cleanup
  public cleanup(): void {
    this.stopMoving();
    this.stopHealthCheck();
  }

  // Add the sendMessage method
  public sendMessage(message: any): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot send message.');
      return;
    }
    this.logMessage('sent', message);
    this.pixelStreamingHandler.sendEvent(message.type, message.data);
  }

  // ============================================
  // MARKETING MODE COMMUNICATION METHODS
  // ============================================

  // Switch building view
  public switchBuildingView(buildingView: string): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot switch building view.');
      return;
    }
    const message = { buildingView };
    this.logMessage('sent', { type: 'switchBuildingView', data: message });
    this.pixelStreamingHandler.sendEvent('switchBuildingView', message);
  }

  // Jump to specific apartment
  public jumpToApartment(apartmentId: string): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot jump to apartment.');
      return;
    }
    const message = { apartmentId };
    this.logMessage('sent', { type: 'jumpToApartment', data: message });
    this.pixelStreamingHandler.sendEvent('jumpToApartment', message);
  }

  // Set lighting mood
  public setLightingMood(mood: 'day' | 'evening' | 'night'): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot set lighting mood.');
      return;
    }
    const message = { mood };
    this.logMessage('sent', { type: 'setLightingMood', data: message });
    this.pixelStreamingHandler.sendEvent('setLightingMood', message);
  }

  // Set navigation mode
  public setNavigationMode(mode: 'building' | 'tour' | 'drone'): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot set navigation mode.');
      return;
    }
    const message = { mode };
    this.logMessage('sent', { type: 'setNavigationMode', data: message });
    this.pixelStreamingHandler.sendEvent('setNavigationMode', message);
  }

  // Capture photo
  public capturePhoto(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot capture photo.');
      return;
    }
    this.logMessage('sent', { type: 'capturePhoto', data: {} });
    this.pixelStreamingHandler.sendEvent('capturePhoto', {});
  }

  // Start video recording
  public startVideoRecording(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot start video recording.');
      return;
    }
    this.logMessage('sent', { type: 'startVideoRecording', data: {} });
    this.pixelStreamingHandler.sendEvent('startVideoRecording', {});
  }

  // Stop video recording
  public stopVideoRecording(): void {
    if (!this.isStreamReady) {
      console.warn('PixelStreamingCommunicator: Stream not ready, cannot stop video recording.');
      return;
    }
    this.logMessage('sent', { type: 'stopVideoRecording', data: {} });
    this.pixelStreamingHandler.sendEvent('stopVideoRecording', {});
  }
} 